import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Title, Loader, ClearFilters, MedicalSupplyTypeSelect, TablePaginationWrapper } from '../../elements';
import { MedicalSuppliesService } from '../../hooks';
import Grid from '@material-ui/core/Grid';
import {
  Paper,
  Icon,
  TableRow,
  Button,
  TableCell,
  TableBody,
  Table,
  TextField,
  Box,
  TableContainer,
  IconButton,
  Chip,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPills } from '@fortawesome/free-solid-svg-icons';
import { useStyles } from './styles';
import { MedicalSupplyAddModal, MedicalSupplyEditModal } from '../../components';
import { MedicalSupplyType, MedicalSupplyViewModel, medicalSupplyMethodKeys } from '../../models/injection';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import UserRole from '../../constants/user-role';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';

const MedicalSupplies: React.FC<any> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { formatDate } = useFarmDateTime();
  const canManageSupplies =
    state.role === UserRole.admin || (state.role === UserRole.veterinary && (state.businessUnits?.length ?? 0) > 0);

  const medicalSuppliesService = new MedicalSuppliesService();

  const [supplies, setSupplies] = React.useState([] as MedicalSupplyViewModel[]);
  const [loaded, setLoaded] = React.useState(false);
  const [number, setNumber] = React.useState('');
  const [supplyType, setSupplyType] = React.useState(0);

  const [addSupplyOpened, setAddSupplyOpened] = React.useState(false);
  const [editSupply, setEditSupply] = React.useState<MedicalSupplyViewModel | null>(null);
  const [removeSupply, setRemoveSupply] = React.useState<MedicalSupplyViewModel | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadMedicalSupplies = async () => {
    const searchPage = page === 0 ? 1 : page + 1;

    medicalSuppliesService
      .getMedicalSupplies(searchPage, rowsPerPage, number, supplyType)
      .then((supplies) => {
        setSupplies(supplies.data);
        setTotal(supplies.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadMedicalSupplies();
      loadData();
    }
  }, [loaded]);

  const handleCreateSupply = (supply: MedicalSupplyViewModel | null) => {
    setAddSupplyOpened(false);

    if (supply == null) {
      return;
    }

    medicalSuppliesService
      .createMedicalSupply(supply)
      .then(() => {
        notifications.success(t('f-supp-create-ok'));
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const handleRemoveConfirm = () => {
    if (!removeSupply) return;
    setIsRemoving(true);
    medicalSuppliesService
      .deleteMedicalSupply(removeSupply.id)
      .then(() => {
        notifications.success(t('f-supp-remove-ok'));
        setRemoveSupply(null);
        setLoaded(false);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsRemoving(false);
      });
  };

  const renderWaitingTimeCell = (supply: MedicalSupplyViewModel) => {
    return supply.waitingTime === 0 ? '-' : t('f-com-number-days', { number: supply.waitingTime });
  };

  const renderMethodCell = (supply: MedicalSupplyViewModel) => {
    const key = medicalSupplyMethodKeys[supply.method];
    return key ? t(key) : '-';
  };

  const renderMedicalSupplyRow = (supply: MedicalSupplyViewModel) => {
    return (
      <TableRow key={supply.id} hover>
        <TableCell variant="head">{supply.number}</TableCell>
        <TableCell>{supply.producer || '-'}</TableCell>
        <TableCell>{renderWaitingTimeCell(supply)}</TableCell>
        <TableCell>{MedicalSupplyType[supply.supplyType] || '-'}</TableCell>
        <TableCell>{renderMethodCell(supply)}</TableCell>
        <TableCell>{supply.lastUsedAt ? formatDate(supply.lastUsedAt) : '-'}</TableCell>
        {canManageSupplies && (
          <TableCell padding="checkbox">
            <Box display="flex">
              <Tooltip title={t('f-com-edit')} arrow>
                <IconButton size="small" onClick={() => setEditSupply(supply)}>
                  <Icon fontSize="small">edit</Icon>
                </IconButton>
              </Tooltip>
              <Tooltip title={t('f-com-remove')} arrow>
                <IconButton size="small" onClick={() => setRemoveSupply(supply)}>
                  <Icon fontSize="small" color="error">
                    delete
                  </Icon>
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        )}
      </TableRow>
    );
  };

  const clearFilters = () => {
    setNumber('');
    setSupplyType(0);
    setPage(0);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box display="flex" justifyContent={'space-between'} alignItems="center">
          <Grid container spacing={1}>
            <FontAwesomeIcon icon={faPills} size="2x" className={classes.headerIcon} />
            <Grid item>
              <Title>{t('f-supplies')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          {(state.role === UserRole.admin || state.role === UserRole.manager || state.role === UserRole.veterinary) && (
            <Tooltip title={t('f-com-create-new')} arrow>
              <IconButton color="primary" onClick={() => setAddSupplyOpened(true)}>
                <Icon fontSize="default">{'add_circle'}</Icon>
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={1} alignItems={'center'} className={classes.mt2}>
          <Grid item md={5} sm={4} xs={12}>
            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                setLoaded(false);
              }}
            >
              <TextField
                id="numberInput"
                label={t('f-com-number')}
                variant="outlined"
                size="small"
                fullWidth
                value={number}
                InputProps={{
                  endAdornment: number && (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setNumber('')}>
                        <Icon fontSize="small">clear</Icon>
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setNumber(e.target.value)}
              />
            </form>
          </Grid>

          <Grid item md={4} sm={4} xs={12}>
            <MedicalSupplyTypeSelect
              allowAny
              fullWidth
              value={supplyType}
              onSupplyTypeChange={(value) => setSupplyType(value)}
            />
          </Grid>

          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center">
              <ClearFilters onClick={clearFilters} />

              <Button
                id="searchBtn"
                variant="contained"
                color="default"
                fullWidth
                onClick={() => setLoaded(false)}
                startIcon={<Icon>{'search'}</Icon>}
              >
                {t('f-search')}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* data table */}
        <Grid container>
          <Grid item md={12} xs={12}>
            <TablePaginationWrapper
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            >
              <TableContainer component={Paper} elevation={1} className={classes.mt2}>
                <Table>
                  <TableBody>
                    {loaded ? (
                      supplies && supplies.length > 0 ? (
                        <TableRow>
                          <TableCell variant="head">{t('f-com-number')}</TableCell>
                          <TableCell variant="head">{t('f-supplies-producer')}</TableCell>
                          <TableCell variant="head">{t('f-com-withdrawal')}</TableCell>
                          <TableCell variant="head">{t('f-supplies-type')}</TableCell>
                          <TableCell variant="head">{t('f-supplies-method')}</TableCell>
                          <TableCell variant="head">{t('f-last-used')}</TableCell>
                          {canManageSupplies && <TableCell variant="head"></TableCell>}
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell variant="head" className={classes.noDataBox}>
                            {t('f-no-data')}
                          </TableCell>
                        </TableRow>
                      )
                    ) : (
                      <TableRow className={classes.spinnerBox}>
                        <TableCell>
                          {' '}
                          <Loader size={LoaderSize.MEDIUM} isLoading />
                        </TableCell>
                      </TableRow>
                    )}

                    {supplies && supplies.length > 0 && loaded
                      ? supplies.map((supply) => renderMedicalSupplyRow(supply))
                      : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </TablePaginationWrapper>
          </Grid>
        </Grid>
      </Grid>

      <MedicalSupplyAddModal opened={addSupplyOpened} onModalAction={handleCreateSupply} />

      <MedicalSupplyEditModal
        opened={editSupply !== null}
        supply={editSupply}
        onClose={(saved: boolean) => {
          setEditSupply(null);
          if (saved) setLoaded(false);
        }}
      />

      <Dialog open={removeSupply !== null} onClose={() => setRemoveSupply(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('f-com-remove')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('f-supp-remove-confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveSupply(null)} variant="outlined" disabled={isRemoving}>
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={handleRemoveConfirm}
            variant="contained"
            color="secondary"
            disabled={isRemoving}
            startIcon={<Icon>delete</Icon>}
          >
            {t('f-com-remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default MedicalSupplies;
